import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { topUpCardService, TopUpCard, TopUpDenomination } from './topUpCardService';
import { cartService } from '../cart/cartService';

const regionTitle = (region: TopUpDenomination['region']) =>
  region === 'Turkey' ? '🇹🇷 Турция' : '🇮🇳 Индия';

export const TopUpCardsPage = () => {
  const [cards, setCards] = useState<TopUpCard[]>([]);
  useEffect(() => { topUpCardService.list().then(setCards); }, []);

  const variants = useMemo(() => cards.flatMap(card =>
    card.denominations.filter(d => d.isAvailable).map(denomination => ({ card, denomination }))), [cards]);

  return (
    <main className="min-h-screen bg-background px-4 py-8 text-foreground">
      <div className="mx-auto max-w-[90rem]">
        <h1 className="text-4xl font-semibold">Карты пополнения PlayStation</h1>
        <p className="mt-2 text-foreground/50">Все доступные номиналы для аккаунтов Турции и Индии.</p>
        <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
          {variants.map(({ card, denomination }) => (
            <Link key={denomination.id} to={`/top-up-card/${card.id}?denomination=${denomination.id}`} className="group overflow-hidden rounded-3xl border border-foreground/10 bg-card transition hover:-translate-y-1">
              <img src={denomination.picture || card.picture} alt={denomination.name} className="aspect-[5/6] w-full object-cover transition-transform group-hover:scale-[1.03]" />
              <div className="p-4">
                <p className="text-sm text-foreground/55">{regionTitle(denomination.region)}</p>
                <h2 className="mt-1 font-medium">{denomination.faceValue.toLocaleString('ru-RU')} {denomination.currency}</h2>
                <p className="mt-2 text-xl font-semibold">{denomination.price.toLocaleString('ru-RU')} ₽</p>
                <p className="mt-1 text-xs text-foreground/45">{denomination.price.toLocaleString('ru-RU')} ₽ = {denomination.faceValue.toLocaleString('ru-RU')} {denomination.currency}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </main>
  );
};

export const TopUpCardPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [card, setCard] = useState<TopUpCard | null>(null);
  const [selected, setSelected] = useState<TopUpDenomination | null>(null);
  const [added, setAdded] = useState(false);

  useEffect(() => { if (id) topUpCardService.get(Number(id)).then(setCard); }, [id]);
  useEffect(() => {
    if (!card) return;
    const denominationId = Number(searchParams.get('denomination'));
    const initial = card.denominations.find(d => d.id === denominationId && d.isAvailable);
    if (initial) setSelected(initial);
  }, [card, searchParams]);

  if (!card) return <div className="min-h-screen bg-background p-8 text-foreground">Загрузка…</div>;
  const groups = {
    Turkey: card.denominations.filter(d => d.region === 'Turkey' && d.isAvailable),
    India: card.denominations.filter(d => d.region === 'India' && d.isAvailable),
  };

  return (
    <main className="min-h-screen bg-background px-4 py-8 text-foreground">
      <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[380px_1fr]">
        <img src={selected?.picture || card.picture} alt={selected?.name || card.name} className="w-full rounded-[32px] border border-foreground/10 object-cover" />
        <div>
          <Link to="/top-up-cards" className="text-sm text-foreground/45">← Все карты</Link>
          <h1 className="mt-4 text-4xl font-semibold">{card.name}</h1>
          <p className="mt-3 text-foreground/55">{card.description}</p>
          <div className="mt-7 space-y-5">
            {(['Turkey', 'India'] as const).map(region => groups[region].length > 0 && (
              <section key={region}>
                <h2 className="mb-3 font-semibold">{regionTitle(region)}</h2>
                <div className="grid gap-2 sm:grid-cols-2">
                  {groups[region].map(d => (
                    <button key={d.id} onClick={() => { setSelected(d); setAdded(false); }} className={`flex items-center justify-between gap-3 rounded-2xl border p-3 text-left ${selected?.id === d.id ? 'border-red-500 bg-red-500/10' : 'border-foreground/10 bg-card'}`}>
                      {d.picture && <img src={d.picture} alt="" className="h-12 w-12 rounded-xl object-cover" />}
                      <span className="flex-1 font-medium">{d.faceValue.toLocaleString('ru-RU')} {d.currency}</span>
                      <span className="font-semibold">{d.price.toLocaleString('ru-RU')} ₽</span>
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>
          <div className="mt-7 rounded-3xl border border-foreground/10 bg-card p-5">
            <p className="text-3xl font-semibold">{selected ? `${selected.price.toLocaleString('ru-RU')} ₽` : `от ${card.price.toLocaleString('ru-RU')} ₽`}</p>
            <button disabled={!selected} onClick={added ? () => navigate('/cart') : async () => { if (selected?.id) { await cartService.add(selected.id); setAdded(true); } }} className="mt-4 w-full rounded-2xl bg-red-600 p-4 font-semibold text-white disabled:opacity-40">
              {added ? 'Перейти в корзину' : selected ? 'Добавить в корзину' : 'Выберите номинал'}
            </button>
          </div>
          <p className="mt-4 text-xs text-foreground/45">После оплаты вы получите код, который необходимо активировать в аккаунте PlayStation соответствующего региона.</p>
        </div>
      </div>
    </main>
  );
};
